// Batch 31 · item 16 (M84) — Document Review: "Read again" for one document or for the selected documents.
// * a tick on each document that was read or failed — none ticked by default, NO select-all, at most 25 at a time
//   (the cap is said on screen and the button is off above it);
// * "Read again (n selected)" and, on the selected document, "Read again"; both ask first (EN + AR);
// * "Also replace the type I set" — off by default: a type chosen by hand at upload is kept unless it is ticked;
// * the server keeps the DOC reference, the links and the permanent-file tick, replaces the extracted fields and writes
//   one Audit Trail row per document (POST /api/documents/read-again).
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { askConfirm } from "@/lib/appDialog";
import { toast } from "sonner";

export const READ_AGAIN_MAX = 25;
export const READABLE = ["done", "failed"];

export const DRA_TEXT = {
  en: {
    button: "Read again",
    buttonN: "Read again ({n} selected)",
    tick: "Select to read again",
    replaceType: "Also replace the type I set",
    cap: "Up to {max} documents at a time.",
    overCap: "{n} selected — up to {max} at a time.",
    confirmOne: "Read 1 document again? Its extracted fields will be replaced.",
    confirmMany: "Read {n} documents again? Their extracted fields will be replaced.",
    confirmTitle: "Read again",
    queued: "Reading again — {n} queued.",
    failed: "Could not read again.",
  },
  ar: {
    button: "إعادة القراءة",
    buttonN: "إعادة القراءة ({n} محدد)",
    tick: "حدد لإعادة القراءة",
    replaceType: "استبدل أيضاً النوع الذي حددته",
    cap: "حتى {max} مستنداً في كل مرة.",
    overCap: "{n} محدد — الحد {max} في كل مرة.",
    confirmOne: "إعادة قراءة مستند واحد؟ ستُستبدل الحقول المستخرجة منه.",
    confirmMany: "إعادة قراءة {n} مستندات؟ ستُستبدل الحقول المستخرجة منها.",
    confirmTitle: "إعادة القراءة",
    queued: "جارٍ إعادة القراءة — {n} في الانتظار.",
    failed: "تعذرت إعادة القراءة.",
  },
};

const T = (lang) => DRA_TEXT[lang === "ar" ? "ar" : "en"];

export function canReadAgain(doc) {
  return !!doc && READABLE.includes(doc.extraction_status);
}

export function confirmText(n, lang) {
  const L = T(lang);
  return n === 1 ? L.confirmOne : L.confirmMany.replace("{n}", String(n));
}

/** Ask, then start one read per document. Returns true when the reads were queued. */
export async function readAgain(ids, { replaceType = false, lang, onDone } = {}) {
  const L = T(lang);
  if (!ids.length || ids.length > READ_AGAIN_MAX) return false;
  if (!(await askConfirm(confirmText(ids.length, lang), { title: L.confirmTitle }))) return false;
  try {
    const r = await api.post("/documents/read-again", { doc_ids: ids, replace_manual_type: !!replaceType });
    toast.success(L.queued.replace("{n}", String((r && r.data && r.data.count) || ids.length)));
    if (onDone) onDone();
    return true;
  } catch (e) {
    toast.error(toErrMsg(e, L.failed));
    return false;
  }
}

/** The ticks and "Also replace the type I set" of one engagement. PR 45 review L8: both are cleared when the engagement
 *  changes, so a tick made on one engagement is never sent with another's documents. */
export function useReadAgainSelection(engagementId) {
  const [sel, setSel] = useState(() => new Set());
  const [replace, setReplace] = useState(false);
  useEffect(() => { setSel(new Set()); setReplace(false); }, [engagementId]);
  const toggle = (id, on) => setSel((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  const clear = () => setSel(new Set());
  return { sel, toggle, clear, replace, setReplace };
}

/** The tick on a document tile (only for a document that was read or failed). */
export function ReadAgainTick({ doc, checked, onToggle, lang }) {
  if (!canReadAgain(doc)) return null;
  return (
    <input type="checkbox" className="mt-1 h-3.5 w-3.5 shrink-0" checked={!!checked} aria-label={T(lang).tick}
           title={T(lang).tick} onClick={(ev) => ev.stopPropagation()}
           onChange={(ev) => onToggle && onToggle(doc.id, ev.target.checked)}
           data-testid={`doc-read-again-tick-${doc.id}`} />
  );
}

/** The bar above the list: "Read again (n selected)", the replace-type tick and the cap note. No select-all. */
export function ReadAgainBar({ selectedIds, replaceType, onReplaceType, lang, onDone }) {
  const L = T(lang);
  const ids = Array.from(selectedIds || []);
  const n = ids.length;
  const over = n > READ_AGAIN_MAX;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-3 py-1.5 text-[11px]" data-testid="doc-read-again-bar">
      <button type="button" disabled={n === 0 || over}
              className="rounded border px-2 py-0.5 text-sky-700 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => readAgain(ids, { replaceType, lang, onDone })}
              data-testid="doc-read-again-selected">
        {L.buttonN.replace("{n}", String(n))}
      </button>
      <label className="inline-flex items-center gap-1">
        <input type="checkbox" className="h-3.5 w-3.5" checked={!!replaceType}
               onChange={(ev) => onReplaceType && onReplaceType(ev.target.checked)} data-testid="doc-read-again-replace-type" />
        <span>{L.replaceType}</span>
      </label>
      <span className={over ? "text-red-600" : "text-muted-foreground"} data-testid="doc-read-again-cap">
        {over ? L.overCap.replace("{n}", String(n)).replace("{max}", String(READ_AGAIN_MAX)) : L.cap.replace("{max}", String(READ_AGAIN_MAX))}
      </span>
    </div>
  );
}

/** "Read again" on the selected document (header of the list). */
export function ReadAgainButton({ doc, replaceType, lang, onDone }) {
  if (!canReadAgain(doc)) return null;
  return (
    <button type="button" className="rounded border px-2 py-1 text-xs text-sky-700 hover:bg-sky-50"
            onClick={() => readAgain([doc.id], { replaceType, lang, onDone })}
            data-testid={`doc-read-again-${doc.id}`}>
      {T(lang).button}
    </button>
  );
}
